import { ICustomPost, IStartTime } from 'static/interfaces'

export interface ISelectedDate {
  startDate: null | moment.Moment
  endDate: null | moment.Moment
}

export interface ISelectedGuests {
  adults: number
  children: number
  infants: number
}

export interface IPostPrice {
  age_max: number | null
  age_min: number | null
  created_at: string
  id: number
  max_people: number
  participant_type: string
  participant_type_i18n: string
  price: number
  pricing_type: string
  pricing_type_i18n: string
  updated_at: string
}

export interface IPriceBreakdownData {
  count: number
  price: number | null
  calcPrice: number | null
}

export interface IPriceBreakdown {
  adult: IPriceBreakdownData | null
  child: IPriceBreakdownData | null
  infant: IPriceBreakdownData | null
  private: IPriceBreakdownData | null
}

export interface IGroupedStartTimes {
  postId: number
  startTimes: IStartTime[]
}

export interface IAges {
  child: {
    min: number | null
    max: number | null
  }
  infant: {
    min: number | null
    max: number | null
  }
}

const SENTRY_MASKED = '***'
const SENTRY_SENSITIVE_KEYS = ['fullname', 'email', 'tel', 'pickup_location']

const maskEmail = (value: string) => {
  const [localPart, domainPart] = value.split('@')
  if (!domainPart) {
    return SENTRY_MASKED
  }
  return `${localPart.slice(0, 2)}${SENTRY_MASKED}@${domainPart}`
}

const maskTel = (value: string) => {
  if (value.length <= 4) {
    return SENTRY_MASKED
  }
  return `${SENTRY_MASKED}${value.slice(-4)}`
}

const maskSensitiveValueByKey = (key: string, value: any) => {
  if (typeof value !== 'string') {
    return value
  }

  if (key === 'email') {
    return maskEmail(value)
  }
  if (key === 'tel') {
    return maskTel(value)
  }
  if (SENTRY_SENSITIVE_KEYS.includes(key)) {
    return SENTRY_MASKED
  }
  return value
}

export const sanitizeReservationParamsForSentry = (value: any, keyName = ''): any => {
  if (Array.isArray(value)) {
    return value.map(item => sanitizeReservationParamsForSentry(item))
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((result, key) => {
      result[key] = sanitizeReservationParamsForSentry(value[key], key)
      return result
    }, {})
  }
  return maskSensitiveValueByKey(keyName, value)
}

const getPriceObject = (price: number, count: number): IPriceBreakdownData => {
  return {
    count,
    price,
    calcPrice: price * count,
  }
}

const priceOrDefault = (value: any, defaultValue: number) => {
  return isNaN(value) ? defaultValue : value
}

/**
 * 人数に応じた料金内訳を作成、価格未設定時は0円で返却
 * @param post_prices 料金設定配列
 * @param guests 選択人数
 * @param isPrivate プライベートツアーかどうか
 * @returns 料金内訳オブジェクト
 */
export const getPriceBreakdown = (
  post_prices: IPostPrice[],
  guests: ISelectedGuests,
  isPrivate: boolean
): IPriceBreakdown => {
  if (post_prices.length === 0) {
    return {
      adult: isPrivate ? null : getPriceObject(0, guests.adults),
      child: isPrivate ? null : getPriceObject(0, guests.children),
      infant: isPrivate ? null : getPriceObject(0, guests.infants),
      private: isPrivate ? getPriceObject(0, guests.adults) : null,
    }
  }

  if (isPrivate) {
    const privatePrices = post_prices
      .filter(price => price.pricing_type === 'private')
      .sort((a, b) => a.max_people - b.max_people)
    const privatePrice =
      privatePrices.find(price => price.max_people >= guests.adults) ||
      privatePrices[privatePrices.length - 1]

    const privatePriceAmount = privatePrice ? priceOrDefault(privatePrice.price, 0) : 0

    return {
      adult: null,
      child: null,
      infant: null,
      private: getPriceObject(privatePriceAmount, guests.adults),
    }
  }

  const adult = post_prices.find(price => price.participant_type === 'adult')
  const child = post_prices.find(price => price.participant_type === 'child')
  const infant = post_prices.find(price => price.participant_type === 'infant')

  const adultPrice = priceOrDefault(adult?.price, 0)
  const childPrice = priceOrDefault(child?.price, adultPrice)
  const infantPrice = priceOrDefault(infant?.price, adultPrice)
  return {
    adult: getPriceObject(adultPrice, guests.adults),
    child: getPriceObject(childPrice, guests.children),
    infant: getPriceObject(infantPrice, guests.infants),
    private: null,
  }
}

const getStartTimeMinutes = (startTime: string): number => {
  const [hours, minutes] = startTime.split(':').map(Number)
  return hours * 60 + minutes
}

export const groupStartTimesByPost = (startTimes: IStartTime[]): IGroupedStartTimes[] => {
  const groupsByPost = new Map<number, IStartTime[]>()

  startTimes.forEach(startTime => {
    const group = groupsByPost.get(startTime.post_id) || []
    group.push({ ...startTime })
    groupsByPost.set(startTime.post_id, group)
  })

  return Array.from(groupsByPost.entries()).map(([postId, postStartTimes]) => ({
    postId,
    startTimes: postStartTimes
      .map((startTime, index) => ({ startTime, index }))
      .sort((a, b) => {
        const difference =
          getStartTimeMinutes(a.startTime.start_time) - getStartTimeMinutes(b.startTime.start_time)
        return difference || a.index - b.index
      })
      .map(({ startTime }) => startTime),
  }))
}

/**
 * 時間枠は、次の条件をすべて満たす場合に予約可能とする。
 * - 予約済み扱いではない（reserved が false）。
 * - 空き数（available_count）が1以上ある。
 * - 予約人数上限（limit_people）が選択人数以上である。
 * 選択人数（guestCount）は大人＋子どもで、幼児は含めない。
 * 年齢制限・購入権限による Choose ボタンの可否は、この時間枠の判定とは別に扱う。
 */
export const isStartTimeAvailable = (startTime: IStartTime, guestCount: number): boolean => {
  return (
    !startTime.reserved && startTime.available_count > 0 && startTime.limit_people >= guestCount
  )
}

export const shouldShowStartTimeLeft = (startTime: IStartTime, guestCount: number): boolean => {
  return isStartTimeAvailable(startTime, guestCount) && startTime.available_count <= 5
}

export const getAges = (post_prices: IPostPrice[] | undefined): IAges => {
  if (!post_prices) {
    return {
      child: {
        min: null,
        max: null,
      },
      infant: {
        min: null,
        max: null,
      },
    }
  }
  const child = post_prices.find(price => price.participant_type === 'child')
  const infant = post_prices.find(price => price.participant_type === 'infant')
  return {
    child: {
      min: child?.age_min || null,
      max: child?.age_max || null,
    },
    infant: {
      min: infant?.age_min || null,
      max: infant?.age_max || null,
    },
  }
}

export const getLimitParticipantNote = (post: ICustomPost, ages: IAges) => {
  if (post.limit_participant_type !== 'child' && post.limit_participant_type !== 'infant') {
    return null
  }

  const noAgeNote = `Guests ${post.limit_participant_type} cannot participate`
  if (post.limit_participant_type === 'child') {
    if (!ages.child.max) {
      return noAgeNote
    }
    return `Guests ${post.limit_participant_type} (Under ${ages.child.max}) cannot participate`
  }
  if (post.limit_participant_type === 'infant') {
    if (!ages.infant.max) {
      return noAgeNote
    }
    return `Guests ${post.limit_participant_type} (Under ${ages.infant.max}) cannot participate`
  }

  return null
}

export const getAgeLabelOnPrice = (
  label: 'Adults' | 'Children' | 'Infants' | 'Guests',
  ages: IAges
) => {
  switch (label) {
    case 'Adults':
      return ages?.child.max ? `(${ages.child.max}-)` : ''
    case 'Children':
      if (ages?.child.min && ages?.child.max) {
        return `(${ages.child.min}-${ages.child.max})`
      } else if (ages?.infant.max) {
        return `(${ages.infant.max}-)`
      }
      return ''
    case 'Infants':
      if (ages?.infant.max) {
        return `(Under ${ages.infant.max})`
      } else if (ages?.child.min) {
        return `(Under ${ages.child.min})`
      }
      return ''
    default:
      return ''
  }
}
